import get from 'lodash.get';
import { mapGetters, mapMutations } from 'vuex';
import { isAfter } from 'date-fns';
import { FormField, FormInput, Alert } from '@/components';
import { AccessLevel, NonDependantFlexCodes, OperationType, InsurableTypeEnum } from '@/enums';
import { VUEX_GENERAL, VUEX_MEMBER_DETAILS } from '@/constants/VuexConstants';
import { formatDate, getStudentDeclarationYear, isPastDateIncludingToday, reverseFormatFormDate, getCurrentDate, withinStudentAge } from '@general/js/utils';
import { showConcessionCardDetailsForm, getAccessLevel, isEmptyString } from '@/utils';
import { ProductTypeCodes } from '@general/js/enums';

export default {
  components: {
    FormField,
    FormInput,
    Alert,
  },
  data() {
    return {
      AccessLevel,
      OperationType,
    };
  },

  mounted() {
    if (this.v$) {
      this.updateMemberDetailsFormValid(this.v$.$invalid);
      this.$emit('updateStepValidation', this.v$.$invalid, this.$options.name);
    }
  },
  methods: {
    ...mapMutations('MemberDetails', {
      updateMemberDetails: VUEX_MEMBER_DETAILS.MUTATIONS.updateMemberDetails,
      updateMemberDetailsFormValid: VUEX_MEMBER_DETAILS.MUTATIONS.updateMemberDetailsFormValid,
      updateMemberDetailsRefData: VUEX_MEMBER_DETAILS.MUTATIONS.updateMemberDetailsRefData,
      updateMemberDetailsPageNum: VUEX_MEMBER_DETAILS.MUTATIONS.updateMemberDetailsPageNum,
      resetMemberDetails: VUEX_MEMBER_DETAILS.MUTATIONS.resetMemberDetails,
      updateSubmitUpdateSuccessful: VUEX_MEMBER_DETAILS.MUTATIONS.updateSubmitUpdateSuccessful,
      updateSubmitPolicyInProcess: VUEX_MEMBER_DETAILS.MUTATIONS.updateSubmitPolicyInProcess,
      updateMemberPolicies: VUEX_MEMBER_DETAILS.MUTATIONS.updateMemberPolicies,
    }),
    accessLevel(policyNumber) {
      return getAccessLevel(policyNumber, this.memberships);
    },
    getFullAddress(addressObjectKey, isPoBox = false) {
      const addressObject = get(this.memberDetails, addressObjectKey);
      const { addressLine1, addressLine2, addressLine3, postcode, state, suburb } = addressObject;

      if (addressObjectKey === 'contactInformation.postalAddress' && isPoBox) {
        // format `po box` to be always `PO Box`
        const poBox = get(this.memberDetails, addressObjectKey)
          .addressLine1.toLowerCase()
          .replace(/(po\sb)/, 'PO B');
        return [poBox, suburb?.toUpperCase(), postcode, state?.toUpperCase()].filter(Boolean).join(' ');
      }

      return [addressLine1, addressLine2, addressLine3, suburb?.toUpperCase(), postcode, state?.toUpperCase()].filter(Boolean).join(' ');
    },
    updateValidation(data) {
      this.updateMemberDetailsFormValid(data);
    },

    /**
     * Searches the given array and returns the index of the first Veteran Card without an end-date that it can find.
     * If no such object exists, returns -1.
     *
     * @returns {int} The index of the found Veteran Card object, or -1 if no match occurs.
     */
    findFirstActiveVeteranCardIndex(veteranCardListToSearch) {
      if (veteranCardListToSearch == null) return -1;

      // NOTE: `findIndex` will return -1 if no match found.
      /**
       * TODO: There is a possibility of having multiple active cards, one that has been end dated in the future, while
       * another card has no end date. Need to determine what needs to happen here. For the moment, only get the first item that
       * matches the criteria.
       */
      return veteranCardListToSearch.findIndex((card) => {
        return this.checkIfItemIsActive(card);
      });
    },

    /**
     * Returns an array of active accepted conditions from the passed-in Veteran Card details.
     */
    filterActiveAcceptedConditions(card) {
      if (card && card.acceptedConditions) {
        // Get active accepted conditions only.
        const filteredActiveAcceptedConditions = card.acceptedConditions.filter((condition) => {
          return this.checkIfItemIsActive(condition);
        });

        return filteredActiveAcceptedConditions;
      }
      return [];
    },

    /**
     * Check whether the passed-in item is active. This function should only be used with items that have both a
     * `startDate` and `endDate`, such as Veteran Card and accepted conditions.
     *
     * @param {object} item Any object with a `startDate` and `endDate` property.
     * @returns {boolean} True if the passed-in item is active, or false otherwise.
     */
    checkIfItemIsActive(item) {
      // If there is endDate and is today or in the past
      if (item.endDate && isPastDateIncludingToday(this.reverseFormatFormDate(item.endDate))) {
        return false;
      }

      // Only consider items with a start date in the past including today's date.
      if (isPastDateIncludingToday(this.reverseFormatFormDate(item.startDate))) {
        // If there is no end date OR if the end date is a future date, then the item is active.
        if (isEmptyString(item.endDate) || !isPastDateIncludingToday(this.reverseFormatFormDate(item.endDate))) {
          return true;
        }
      }

      return false;
    },

    /**
     * Iterates over the given array of Veteran Card objects and end-dates all those cards and accepted conditions without an end-date.
     * If any cards or accepted conditions already have an earlier end-date, they will be skipped.
     *
     * @param {Array} existingVeteranCardDetails Array of Veteran Card detail objects.
     * @returns A new array of end-dated Veteran Card detail objects.
     */
    endDateActiveVeteranCards(existingVeteranCardDetails) {
      if (existingVeteranCardDetails !== undefined) {
        return existingVeteranCardDetails.map((card) => {
          // Check if there is no end date before setting one, otherwise we may be overwriting existing end dates.
          if (card.endDate == null || card.endDate === '' || isAfter(new Date(card.endDate), new Date())) {
            const endDate = getCurrentDate();
            card.endDate = endDate;
            card.op = OperationType.REMOVE;

            card.acceptedConditions.forEach((acceptedCondition) => {
              // Check if there is no end date before setting one, otherwise we may be overwriting existing end dates.
              if (acceptedCondition.endDate == null || acceptedCondition.endDate === '' || isAfter(new Date(acceptedCondition.endDate), new Date())) {
                acceptedCondition.op = OperationType.REMOVE;
                acceptedCondition.endDate = endDate;
              }
            });
          }
          return card;
        });
      }
      return [];
    },

    isPolicyHolder(membership) {
      return membership.relationship === NonDependantFlexCodes.POLICY_HOLDER;
    },

    isEmptyString,
    formatDate,
    reverseFormatFormDate,
    getCurrentDate,
    withinStudentAge,
  },
  computed: {
    ...mapGetters({
      currentMemberDetail: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      currentPolicyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      isAutoLogout: VUEX_GENERAL.GETTERS.getLogoutState,
      currentPersonCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      memberships: VUEX_GENERAL.GETTERS.memberships,
      membershipInfo: VUEX_GENERAL.GETTERS.getMembershipInfo,
    }),
    ...mapGetters('MemberDetails', {
      pageNumber: VUEX_MEMBER_DETAILS.GETTERS.getMemberDetailsPageNum,
      memberDetails: VUEX_MEMBER_DETAILS.GETTERS.getMemberDetails,
      refData: VUEX_MEMBER_DETAILS.GETTERS.getMemberDetailsPageReferenceData,
      isInvalid: VUEX_MEMBER_DETAILS.GETTERS.getMemberDetailsFormValidationStatus,
      coverDetails: VUEX_MEMBER_DETAILS.GETTERS.getCoverDetails,
      submitPolicyInProcess: VUEX_MEMBER_DETAILS.GETTERS.getSubmitPolicyInProcess,
      memberPolicies: VUEX_MEMBER_DETAILS.GETTERS.getMemberPolicies,
    }),
    isDependant() {
      const nonDependantRelationships = Object.values(NonDependantFlexCodes);
      const covered = this.memberDetails?.memberInformation?.coveredOnMembershipAsCode?.toUpperCase();
      return !nonDependantRelationships.includes(covered);
    },
    memberDetailsPersonCode() {
      return this.$route.params.personCode;
    },
    loggedInUserAccessingThemselves() {
      // Only display the keep private flag field if the logged in member is accessing themselves
      if (this.memberDetailsPersonCode === this.currentMemberDetail.personCode) {
        return true;
      }
      return false;
    },
    getCoveredOnMembershipText() {
      return this.refData.coveredOnMembershipOptions.find((option) => {
        return option?.returnValue === this.memberDetails?.memberInformation?.coveredOnMembershipAsCode;
      })?.display;
    },
    getCurrentTitle() {
      /**
       * MultiSelect SFC always need to have arr as selectedItems prop
       * find currently option object of currently selected title/rank
       */
      const currentTitleObj = this.refData.titleRankOptions.find((option) => {
        return option.returnValue === this.memberDetails?.memberInformation?.title;
      });
      const returnArr = [];

      // if there is matched one, append it to return arr
      if (currentTitleObj !== undefined) {
        returnArr.push(currentTitleObj);
      }

      return returnArr;
    },
    currentMembership() {
      const memberships = this.currentMemberDetail.memberships.filter((membership) => {
        return membership.memberNumber === this.currentPolicyNumber;
      });
      let currentMembership = null;
      if (memberships.length > 1) {
        logger.error('There are too many memberships that are not unique');
      } else {
        currentMembership = memberships[0];
      }
      return currentMembership;
    },
    studentDeclarationStatus: {
      get() {
        // Check if user declared before (start date checking against the result of "getStudentDeclarationYear" and has the institute name
        // returned value of "studentDeclarationStatus" will be used for deselecting or toggling YES/NO buttons

        // If there is student declaration data being returned from the server and it is for the current declaration year, then return true for YES/NO option(option value).
        // option will be selected as YES
        if (Number(getStudentDeclarationYear(this.memberDetails?.studentDeclaration?.startDate)) === Number(getStudentDeclarationYear(new Date())) && this.instituteName) {
          return true;
        }

        // If the user select YES and wants to declare as a student
        if (this.isStudent) {
          return true;
        }

        // Otherwise return null because there is no previous student declaration.
        return null;
      },

      set(newValue) {
        return newValue;
      },
    },
    concessionCardDeclarationStatus: {
      get() {
        // Based on the condition in "showConcessionCardDetailsForm" set the declaration status True/False
        if (
          showConcessionCardDetailsForm(
            this.memberDetails?.memberInformation?.residentialAddress?.state,
            this.memberDetails?.memberInformation?.dateOfBirth,
            this.memberDetails?.membershipSummary?.extrasOnly,
            this.memberDetails?.concessionCard?.hasConcessionCard,
          )
        ) {
          return true;
        }

        return false;
      },

      set(newValue) {
        return newValue;
      },
    },
    currentAccessLevel() {
      return this.accessLevel(this.currentPolicyNumber);
    },
    composition() {
      return this.coverDetails?.membership.composition;
    },
    memberCover() {
      // Map member being accessed to response from Retrieve Cover Details
      return this.coverDetails?.members.filter((member) => {
        return member.personCode === this.memberDetailsPersonCode;
      })[0];
    },
    studentDependents() {
      return this.coverDetails?.members.filter((person) => {
        return person.insurableType === InsurableTypeEnum.STUDENT;
      }).length;
    },
    nonStudentDependent() {
      return this.coverDetails?.members.filter((person) => {
        return person.insurableType === InsurableTypeEnum.NON_STUDENT;
      }).length;
    },
    extrasCover() {
      return this.coverDetails?.products.some((product) => {
        return product.productTypeCode === ProductTypeCodes.EXTRAS_ONLY;
      });
    },
    hospitalCover() {
      return this.coverDetails?.products.some((product) => {
        return product.productTypeCode === ProductTypeCodes.HOSPITAL_ONLY;
      });
    },
    packageCover() {
      return this.coverDetails?.products.some((product) => {
        return product.productTypeCode === ProductTypeCodes.PACKAGE;
      });
    },
    extrasOnly() {
      return this.extrasCover && !this.hospitalCover && !this.packageCover;
    },
    hospitalOnly() {
      return this.hospitalCover && !this.extrasCover && !this.packageCover;
    },
    hospitalExtrasCover() {
      return (this.hospitalCover && this.extrasCover) || this.packageCover;
    },
    multiplePolicyHolderMemberships() {
      return this.memberPolicies?.filter(this.isPolicyHolder).length > 1;
    },
    multipleMembershipsAndAtLeastOnePolicyHolder() {
      return this.memberPolicies?.length > 1 && this.memberPolicies?.some(this.isPolicyHolder);
    }
  },
  watch: {
    'v$.$invalid': function (newVal) {
      this.updateMemberDetailsFormValid(newVal);
      this.$emit('updateStepValidation', this.v$.$invalid, this.$options.name);
    },

    'v$.$error': function (newVal) {
      this.updateMemberDetailsFormValid(newVal);
    },
  },

  created() {
    this.$watch(
      () => this.v$?.memberDetails?.$model,
      (newVal) => {
        if (newVal !== undefined) {
          this.updateMemberDetails(newVal);
        }
      },
      { deep: true },
    );
  },
};
